import { Model } from "@vuex-orm/core";
import { DateTime, Duration } from "luxon";
import { getBrowserIanaTimeZone as localTz } from "../../helper/browser-time-zone";
import { Enums } from "../../helper/enums";
import PrototypeInstance from "../../helper/prototype-instance";
import CustomEventEmitter from "../../helper/event-emitter";
import { clientEntity } from "./client-entity";
import { commentAttachmentEntity } from "./comment-attachment-entity";
import { contactViewingEntity } from "./contact-viewing-entity";
import { technicianEntity } from "./technician-entity";
import { ticketEntity } from "./ticket-entity";
const eventEmitter = CustomEventEmitter.getInstance();
import { marked } from "marked";
import { clientLocationEntity } from "./client-location-entity";
import {
  _convertMarkdownToHTMLForComments,
  isAutomationRuleComment,
} from "../../entity-service/ticket-entity-service";
import { prepareCommentHtmlForDisplay } from "../../helper/comment-html-links";
import { sanitizeRichText } from "../../helper/html-sanitizer";
import {
  formatPortalChatMessageTimestamp,
  formatPortalChatMessageTimestampHover,
} from "../../helper/portal-datetime-format";
import { contactEntity } from "./contact-entity";
import { _clientFacingNameParts } from "../../entity-service/technician-entity-service";

/** Parses `createdOn` as UTC ISO.
 * Empty/invalid strings return null (Luxon would otherwise format as "Invalid DateTime"). */
function createdOnAsUtc(raw: string | null | undefined): DateTime | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (s === "") return null;
  const dt = DateTime.fromISO(s, { zone: "utc" });
  return dt.isValid ? dt : null;
}

/** Keeps message text above attachment chips with consistent spacing in the chat timeline. */
export function wrapCommentBodyWithAttachments(
  bodyHtml: string,
  attachmentsHtml: string,
): string {
  const body = (bodyHtml || "").trim();
  const att = (attachmentsHtml || "").trim();
  if (!body && !att) {
    return "";
  }
  if (!body) {
    return att;
  }
  if (!att) {
    return body;
  }
  return `${att}<div style="margin-top: var(--space-3);">${body}</div>`;
}

function isImageFileName(name: string): boolean {
  const ext = (name.split(".").pop() || "").toLowerCase();
  return ["png", "jpg", "jpeg", "gif", "bmp", "webp", "bitmap"].includes(ext);
}

function isPdfFileName(name: string): boolean {
  return /\.pdf$/i.test(String(name || "").trim());
}

function attachmentDisplayKey(row: any): string {
  const path = String(row?.blobPath || "")
    .split("?")[0]
    .trim();
  const name = String(row?.name || "attachment").trim();
  return `${path}|${name}`;
}

/** Resolves attachment rows for rendering when the hasMany relation is not hydrated on the model. */
export function attachmentsForCommentDisplay(self: any): unknown[] {
  const id = self?.id;
  const rel = self?.attachments;
  let rows: unknown[] = [];
  if (Array.isArray(rel) && rel.length > 0) {
    rows = rel.filter((row: any) => row && !row.isDeleted);
  } else if (id != null && id !== "") {
    rows = commentAttachmentEntity
      .query()
      .where("commentId", String(id))
      .all()
      .filter((row: any) => !row.isDeleted);
  }
  if (rows.length <= 1) {
    return rows;
  }
  const seen = new Set<string>();
  return rows.filter((row: any) => {
    const key = attachmentDisplayKey(row);
    if (!key || key === "|attachment") {
      return true;
    }
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export type TimelineAttachmentChip = {
  blobPath: string;
  name: string;
  isImage: boolean;
  isPdf: boolean;
};

/** Rows for timeline attachment chips (render with Vue `<Icon>`, not inside `v-html`). */
export function listTimelineAttachmentChips(
  attachments: unknown,
): TimelineAttachmentChip[] {
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return [];
  }
  const out: TimelineAttachmentChip[] = [];
  for (const raw of attachments) {
    const att = raw as {
      blobPath?: string;
      name?: string;
      isDeleted?: boolean;
    };
    if (!att || att.isDeleted) continue;
    const path = String(att.blobPath || "").trim();
    const name = String(att.name || "attachment");
    if (!path) continue;
    out.push({
      blobPath: path,
      name,
      isImage: isImageFileName(name),
      isPdf: isPdfFileName(name),
    });
  }
  return out;
}

function loggedInTechnicianId(): number | undefined {
  const id = PrototypeInstance.globalProperties?.$LoggedInUser?.technicianId;
  return typeof id === "number" && Number.isFinite(id) ? id : undefined;
}

/** Resolves API timeline payloads that may be a raw id or `{ technicianId }`. */
function normalizeTechnicianIdRef(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === "string") {
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  }
  if (typeof raw === "object") {
    const o = raw as { technicianId?: unknown };
    if ("technicianId" in o) {
      return normalizeTechnicianIdRef(o.technicianId);
    }
  }
  return null;
}

function filterTimelineTechnicianRefs(raw: unknown): unknown[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((x) => x != null && x !== "");
}

export interface ticketCommentEntity {
  type: string;
  ticketId: string;
  comment: string;
  markdownComment: string | null;
  markdownWithoutSignature: string | null;
  statusId: number;
  attachments: any;
  sourceId: number;
  isInternal: boolean | null;
  isPinned: boolean;
  isPrimary: boolean | null;
  isDeleted: boolean | null;
  inboundEmailId: number;
  createdByContactId: number;
  createdByTechnicianId: number;
  ruleId: string | null;
  ruleName: string | null;
  serviceProviderId: number;
  createdOn: string;
  updatedOn: string;
  updatedByContactId: number;
  updatedByTechnicianId: number;
  id: string;
  ticketTimeLineType: string | null;
  action: string | null;
  previousValue: string | null;
  newValue: string | null;
  tick: number;
  IsEditTimeLine: boolean;
  isCommentInEditMode: boolean;
  ticketTimeId: number;
  technicianId: number;
  timeInMinutes: number;
  ticketTimeDate: string | null;
  ticketTimeEndDate: string | null;
  previousTimeSpentValue: string | null;
  isUpdate: boolean;
  productId: number;
  productName: string;
  viewOriginalComment: boolean;
  deletedSecondaryTechnicians: any;
  newSecondaryTechnicians: any;
  secondaryTechnicians: any;
  mergedTicketTitle: string | null;
  mergedTicketPrefix: string | null;
  mergedToTicketId: string | null;
  mergedToTicketTitle: string | null;
  mergedToTicketPrefix: string | null;
  isMerged: boolean;
  isNewComment: boolean;
  statusChangedMessage: string | null;
  groupIds: any;
  thirdPartyConversationId: number;
  approvalId: string;
  distance: number;
  deletedWatchers: any;
  newWatchers: any;
  watchers: any;
  reactions: any;
  todoId: any;
  sourceCreatedByEmail: string | null;
  trackingNumber: string | null;
  trackingId: string | null;
  slug: string | null;
  checkpoint: any;
  isOverrideType: boolean;
  contactCcEmail: any;
  showMoreComment: boolean;
  isSuccess: boolean;
  /** Client-only: API failure message for optimistic chat rows. */
  postFailureMessage: string | null;
  mentions: any;
  showAuthorNameAndDate: boolean;
  /** UI-only: show time above sent bubble (hidden when grouped with previous own message). */
  showSentMessageTime: boolean;
  ticketMacroId: number;
  mentionedTechnicianGroupIds: any;
  commentUrl: string | null;
  showViewOriginalButton: boolean;
  isLoadingCommentFromIFrame: boolean;
  emailSent: number;
  showTooltip: boolean;
  tooltipText: string | null;
  isCommentCreated: boolean;
}

export class ticketCommentEntity extends Model {
  static entity = "ticketCommentEntity";
  static primaryKey = "id";

  static fields() {
    return {
      type: this.string(""),
      ticketId: this.string(""),
      comment: this.string(""),
      markdownComment: this.attr(null),
      markdownWithoutSignature: this.attr(null),
      statusId: this.number(0),
      attachments: this.hasMany(commentAttachmentEntity, "commentId", "id"),
      sourceId: this.number(0),
      isInternal: this.boolean(null),
      isPinned: this.boolean(false),
      isPrimary: this.boolean(null),
      isDeleted: this.boolean(null),
      inboundEmailId: this.number(0),
      createdByContactId: this.number(0),
      createdByTechnicianId: this.number(0),
      ruleId: this.string(""),
      ruleName: this.string(""),
      serviceProviderId: this.number(0),
      createdOn: this.attr(""),
      updatedOn: this.attr(""),
      updatedByContactId: this.number(0),
      updatedByTechnicianId: this.number(0),
      id: this.string(""),
      ticketTimeLineType: this.attr(null),
      action: this.attr(null),
      previousValue: this.attr(null),
      newValue: this.attr(null),
      tick: this.number(0),
      IsEditTimeLine: this.boolean(false),
      isCommentInEditMode: this.boolean(false),
      ticketTimeId: this.number(0),
      technicianId: this.number(0),
      timeInMinutes: this.number(0),
      ticketTimeDate: this.attr(null),
      ticketTimeEndDate: this.attr(null),
      previousTimeSpentValue: this.attr(null),
      isUpdate: this.boolean(false),
      productId: this.number(0),
      productName: this.string(""),
      viewOriginalComment: this.boolean(false), //To be used in UI only
      deletedSecondaryTechnicians: this.attr(null),
      newSecondaryTechnicians: this.attr(null),
      secondaryTechnicians: this.attr(null),
      mergedTicketTitle: this.attr(null),
      mergedTicketPrefix: this.attr(null),
      mergedToTicketId: this.attr(null),
      mergedToTicketTitle: this.attr(null),
      mergedToTicketPrefix: this.attr(null),
      isMerged: this.boolean(false),
      isNewComment: this.boolean(false),
      statusChangedMessage: this.attr(null),
      groupIds: this.attr(null),
      thirdPartyConversationId: this.number(0),
      approvalId: this.string(""),
      distance: this.number(0),
      deletedWatchers: this.attr(null),
      newWatchers: this.attr(null),
      watchers: this.attr(null),
      reactions: this.attr(null),
      todoId: this.attr(null),
      sourceCreatedByEmail: this.attr(null),
      trackingNumber: this.string(null),
      trackingId: this.string(null),
      slug: this.string(null),
      checkpoint: this.attr(null),
      isOverrideType: this.boolean(false),
      contactCcEmail: this.attr(null),
      showMoreComment: this.boolean(false),
      isSuccess: this.boolean(true),
      postFailureMessage: this.attr(null),
      mentions: this.attr(null),
      showAuthorNameAndDate: this.boolean(true),
      showSentMessageTime: this.boolean(true),
      ticketMacroId: this.number(0),
      mentionedTechnicianGroupIds: this.attr(null),
      commentUrl: this.attr(null),
      showViewOriginalButton: this.boolean(false), //To be used in UI only
      isLoadingCommentFromIFrame: this.boolean(true),
      emailSent: this.number(0),
      showTooltip: this.boolean(false),
      tooltipText: this.string(null),
      isCommentCreated: this.boolean(true),
    };
  }

  static afterCreate(record: any) {
    if (record != null) {
      let model = {
        ticketId: record.ticketId,
        ticketCommentId: record.id,
      };
      eventEmitter.emit("ticket-comment-added", model);
    }
  }

  get parsedComment() {
    if (this.commentHTML != "") {
      let comment: string = this.commentHTML;

      //if comment contains a body tag

      if (comment.includes("<body")) {
        const parser = new DOMParser();
        const dom = parser.parseFromString(comment, "text/html");
        if (
          dom.getElementsByTagName("body").length > 0 &&
          dom.getElementsByTagName("body")[0]
        ) {
          comment = dom.getElementsByTagName("body")[0].innerHTML;
        }
      }

      //Removing commented code from string
      comment = comment.replace(/<!--[\s\S]*?-->/g, "");

      // Single sanitize point for every timeline/comment `v-html` binding:
      // comment bodies can come from inbound e-mail senders, so they are
      // untrusted. Sanitizing the finished markup means the value bound in the
      // template is DOMPurify-clean whatever the pipeline above produced.
      return sanitizeRichText(prepareCommentHtmlForDisplay(comment));
    }
    return "";
  }

  get isCreatedTimeline() {
    return this.type == "Event" && this.ticketTimeLineType == "Ticket";
  }

  get isFromEmail() {
    return (
      !this.isInternal &&
      (this.createdByContactId > 0 || this.createdByTechnicianId == 0)
    );
  }

  get commentHTML(): string {
    //Inbound Public/TP Comments
    if (this.isFromEmail && this.sourceId != Enums.TicketSource.Chat) {
      if (
        this.markdownWithoutSignature != null &&
        this.markdownWithoutSignature != ""
      ) {
        //Converting Markdown to HTML
        let htmlComment: string = _convertMarkdownToHTMLForComments(
          this.markdownWithoutSignature,
        );
        return (
          `<div class='customiframe'>${htmlComment}</div>` +
          getStyleTagForOutboundIFrame()
        );
      } else if (this.markdownComment != null && this.markdownComment != "") {
        //Converting Markdown to HTML
        let htmlComment: string = _convertMarkdownToHTMLForComments(
          this.markdownComment,
        );
        return (
          `<div class='customiframe'>${htmlComment}</div>` +
          getStyleTagForOutboundIFrame()
        );
      }
      return (
        `<div class='customiframe'>${this.comment || ""}</div>` +
        getStyleTagForOutboundIFrame()
      );
    }
    //Private and Outbound Public/TP Comments
    return (
      `<div class='customiframe'>${this.comment || ""}</div>` +
      getStyleTagForOutboundIFrame()
    );
  }

  get eventCreatedBy(): string {
    if (this.createdByTechnicianId != null && this.createdByTechnicianId > 0) {
      const loggedInId = loggedInTechnicianId();
      if (loggedInId != null && this.createdByTechnicianId === loggedInId) {
        return "You";
      }
      let technician = technicianEntity
        .query()
        .where("technicianId", this.createdByTechnicianId)
        .first();
      return technician ? technician.fullName : "";
    }

    if (this.createdByContactId != null && this.createdByContactId > 0) {
      let contact = contactViewingEntity
        .query<any>()
        .where("contactId", this.createdByContactId)
        .first();
      let name = "";
      if (contact != null) {
        if (
          this.sourceCreatedByEmail != null &&
          this.sourceCreatedByEmail != ""
        ) {
          name =
            contact.fullNameWithoutClientName +
            `(${this.sourceCreatedByEmail})`;
        } else {
          name = contact.fullName;
        }
      }
      return name;
    }
    return "";
  }

  get eventCreatedByLoggedInUser() {
    const loggedInId = loggedInTechnicianId();
    if (
      this.createdByTechnicianId != null &&
      this.createdByTechnicianId > 0 &&
      loggedInId != null &&
      this.createdByTechnicianId === loggedInId
    ) {
      return true;
    }

    return false;
  }

  get lastActionInfo(): string {
    if (this.newValue != null && this.newValue != "") {
      let lastActionId: number = parseInt(this.newValue);
      if (lastActionId == Enums.TicketLastAction.Read) {
        if (
          this.createdByTechnicianId != null &&
          this.createdByTechnicianId > 0
        ) {
          return `marked the ticket as <span class='tertiary--text'>Read</span>`;
        } else {
          return `Ticket was marked as <span class='tertiary--text'>Read</span>`;
        }
      } else if (lastActionId == Enums.TicketLastAction.Unread) {
        if (
          this.createdByTechnicianId != null &&
          this.createdByTechnicianId > 0
        ) {
          return `marked the ticket as <span class='tertiary--text'>Unread</span>`;
        } else {
          return `Ticket was marked as <span class='tertiary--text'>Unread</span>`;
        }
      }
    }
    return "";
  }

  get ticketMacroInfo() {
    if (this.newValue != null && this.newValue != "") {
      let macroModel: any = JSON.parse(this.newValue);

      if (macroModel != null) {
        return `applied Ticket Shortcut <span class='tertiary--text'>${macroModel.name}</span>`;
      }

      return `applied Ticket Shortcut`;
    }
    return "";
  }

  get isGeneratedFromMacro() {
    return this.ticketMacroId != null && this.ticketMacroId > 0;
  }

  get billingOverride() {
    if (this.ticketTimeLineType == "BillingOverride") {
      if (this.isOverrideType == true)
        return "overrode billing settings for all time entries";
      else return "overrode billing settings";
    }
  }

  get updateTitle() {
    if (
      this.previousValue &&
      this.previousValue != "" &&
      this.newValue &&
      this.newValue != ""
    ) {
      return (
        ' updated the title from <span class="tertiary--text">' +
        this.previousValue +
        '</span> to <span class="tertiary--text">' +
        this.newValue +
        "</span>"
      );
    }

    return "";
  }

  get MergedFromTicketTitle() {
    if (this.mergedTicketTitle != null && this.mergedTicketTitle != "") {
      let title: string = "";
      if (this.mergedTicketPrefix != null && this.mergedTicketPrefix != "") {
        title = `${this.mergedTicketPrefix} | ${this.mergedTicketTitle}`;
        return title;
      }
      return this.mergedTicketTitle;
    }
    return "";
  }

  get sentByName() {
    if (this.createdByTechnicianId != null && this.createdByTechnicianId > 0) {
      let technician = technicianEntity
        .query()
        .where("technicianId", this.createdByTechnicianId)
        .first();
      return technician ? technician.fullName : "";
    }

    if (this.createdByContactId != null && this.createdByContactId > 0) {
      let contact = contactViewingEntity
        .query<any>()
        .where("contactId", this.createdByContactId)
        .first();
      let name = "";
      if (contact != null) {
        if (
          this.sourceCreatedByEmail != null &&
          this.sourceCreatedByEmail != ""
        ) {
          name =
            contact.fullNameWithoutClientName +
            `(${this.sourceCreatedByEmail})`;
        } else {
          name = contact.fullName;
        }
      }
      return name;
    }
  }

  get mergedTicketInfo() {
    if (this.mergedTicketTitle != null && this.mergedTicketTitle != "") {
      let ticket = ticketEntity
        .query()
        .where("ticketId", this.ticketId)
        .first();
      let mergedToTicket = ticketEntity
        .query()
        .where("ticketId", this.mergedToTicketId)
        .first();
      let sentence = "";
      if (this.mergedTicketPrefix != null && this.mergedTicketPrefix != "") {
        sentence = `merged <span class='tertiary--text'>${this.mergedTicketPrefix} | ${this.mergedTicketTitle}</span> into `;

        if (mergedToTicket) {
          if (
            mergedToTicket.ticketNumberWithPrefix != null &&
            mergedToTicket.ticketNumberWithPrefix != ""
          ) {
            sentence += `<span class='tertiary--text'>${mergedToTicket.ticketNumberWithPrefix} | ${mergedToTicket.ticketTitle}</span>`;
          } else {
            sentence += `<span class='tertiary--text'>${mergedToTicket.ticketTitle}</span>`;
          }
        } else if (
          this.mergedToTicketTitle != null &&
          this.mergedToTicketTitle != ""
        ) {
          if (
            this.mergedToTicketPrefix != null &&
            this.mergedToTicketPrefix != ""
          ) {
            sentence += `<span class='tertiary--text'>${this.mergedToTicketPrefix} | ${this.mergedToTicketTitle}</span>`;
          } else {
            sentence += `<span class='tertiary--text'>${this.mergedToTicketTitle}</span>`;
          }
          return sentence;
        } else if (ticket) {
          if (
            ticket.ticketNumberWithPrefix != null &&
            ticket.ticketNumberWithPrefix != ""
          ) {
            sentence += `<span class='tertiary--text'>${ticket.ticketNumberWithPrefix} | ${ticket.ticketTitle}</span>`;
          } else {
            sentence += `<span class='tertiary--text'>${ticket.ticketTitle}</span>`;
          }
        } else {
          sentence += "this Ticket";
        }
        return sentence;
      }
    }
    return "";
  }

  get assigneeChange() {
    const leadAssigneeTimeline =
      this.ticketTimeLineType == "Technician" ||
      (this.ticketTimeLineType == "SecondaryTechnician" &&
        filterTimelineTechnicianRefs(this.newSecondaryTechnicians).length ===
          0 &&
        filterTimelineTechnicianRefs(this.deletedSecondaryTechnicians)
          .length === 0);

    //Case When Assignee Add First Time
    if (
      this.previousValue == "" &&
      this.newValue != "" &&
      leadAssigneeTimeline
    ) {
      let technicianName: string = getTechnicianNameByPov(
        parseInt(this.newValue),
        this as any,
      );
      return (
        "assigned <span class='md--text font-weight-bold'>" +
        technicianName +
        "</span> as Lead Assignee"
      );
    }

    //Case When Assignee Remove
    else if (
      this.previousValue != "" &&
      this.newValue == "" &&
      leadAssigneeTimeline
    ) {
      let technicianName: string = getTechnicianNameByPov(
        parseInt(this.previousValue),
        this,
      );
      return (
        "unassigned <span class='md--text font-weight-bold'>" +
        technicianName +
        "</span> as Lead Assignee"
      );
    }

    //Case When Assignee Change to Other Assignee
    else if (
      this.previousValue != "" &&
      this.newValue != "" &&
      leadAssigneeTimeline
    ) {
      let newTechnicianName: string = getTechnicianNameByPov(
        parseInt(this.newValue),
        this,
      );
      let oldTechnicianName: string = getTechnicianNameByPov(
        parseInt(this.previousValue),
        this,
      );
      return (
        "updated Lead Assignee from <span class='md--text font-weight-bold'>" +
        oldTechnicianName +
        "</span> to <span class='md--text font-weight-bold'>" +
        newTechnicianName +
        "</span>"
      );
    }

    return "";
  }

  get secondaryTechnicianChange() {
    let addedTechnicians: string = "";
    let removedTechnicians: string = "";
    const newSecondaryTechnicians: unknown[] = filterTimelineTechnicianRefs(
      this.newSecondaryTechnicians,
    );
    const deletedSecondaryTechnicians: unknown[] = filterTimelineTechnicianRefs(
      this.deletedSecondaryTechnicians,
    );

    if (newSecondaryTechnicians.length > 0) {
      addedTechnicians = getTechnicianNameByPov(
        newSecondaryTechnicians[0],
        this,
      );
      if (newSecondaryTechnicians.length > 1) {
        for (let k = 1; k < newSecondaryTechnicians.length; k++) {
          addedTechnicians +=
            ", " + getTechnicianNameByPov(newSecondaryTechnicians[k], this);
        }
      }
    }

    if (deletedSecondaryTechnicians.length > 0) {
      removedTechnicians = getTechnicianNameByPov(
        deletedSecondaryTechnicians[0],
        this,
      );
      if (deletedSecondaryTechnicians.length > 1) {
        for (let l = 1; l < deletedSecondaryTechnicians.length; l++) {
          removedTechnicians +=
            ", " + getTechnicianNameByPov(deletedSecondaryTechnicians[l], this);
        }
      }
    }

    let sentence: string = "";
    if (addedTechnicians.length > 0) {
      sentence += `added <span class='md--text font-weight-bold'> ${addedTechnicians} </span>`;
      if (removedTechnicians.length > 0) {
        sentence += ` and removed <span class='md--text font-weight-bold'> ${removedTechnicians} </span> from Assisting Technician`;
        return sentence;
      }
      return (sentence += " as Assisting Technician");
    }
    if (removedTechnicians.length > 0) {
      sentence += `removed <span class='md--text font-weight-bold'> ${removedTechnicians} </span> from Assisting Technician`;
      return sentence;
    }

    return "";
  }

  get watcherChange() {
    let addedTechnicians: string = "";
    let removedTechnicians: string = "";
    const newWatchers: unknown[] = filterTimelineTechnicianRefs(
      this.newWatchers,
    );
    const deletedWatchers: unknown[] = filterTimelineTechnicianRefs(
      this.deletedWatchers,
    );

    if (newWatchers.length > 0) {
      addedTechnicians = getTechnicianNameByPov(newWatchers[0], this);
      if (newWatchers.length > 1) {
        for (let k = 1; k < newWatchers.length; k++) {
          addedTechnicians +=
            ", " + getTechnicianNameByPov(newWatchers[k], this);
        }
      }
    }

    if (deletedWatchers.length > 0) {
      removedTechnicians = getTechnicianNameByPov(deletedWatchers[0], this);
      if (deletedWatchers.length > 1) {
        for (let l = 1; l < deletedWatchers.length; l++) {
          removedTechnicians +=
            ", " + getTechnicianNameByPov(deletedWatchers[l], this);
        }
      }
    }

    let sentence: string = "";
    if (addedTechnicians.length > 0) {
      sentence += `added <span class='md--text font-weight-bold'> ${addedTechnicians} </span>`;
      if (removedTechnicians.length > 0) {
        sentence += ` and removed <span class='md--text font-weight-bold'> ${removedTechnicians} </span> from Watchers`;
        return sentence;
      }
      return (sentence += " as Watcher");
    }
    if (removedTechnicians.length > 0) {
      sentence += `removed <span class='md--text font-weight-bold'> ${removedTechnicians} </span> from Watchers`;
      return sentence;
    }

    return "";
  }

  get clientUpdateInfo() {
    if (this.newValue != null && this.newValue != "") {
      let clientId = parseInt(this.newValue);
      let client = clientEntity.find(clientId);
      if (client) {
        return `updated Client to <span class='tertiary--text'>${client.clientName}</span>`;
      }
    }
    return "updated Client";
  }

  get ticketLocationUpdateInfo() {
    if (this.newValue != null && this.newValue != "") {
      let locationId = parseInt(this.newValue);
      let location = clientLocationEntity.find(locationId);
      if (location) {
        return `updated Ticket Location to <span class='tertiary--text'>${
          location!.clientLocationName
        }</span>`;
      }
    }
    return "updated Ticket Location";
  }

  get isCreatedByRule() {
    return isAutomationRuleComment(this.ruleId, this.ruleName);
  }

  get sentByFirstName() {
    if (this.isCreatedByRule) {
      return "Automation";
    }

    if (this.createdByTechnicianId != null && this.createdByTechnicianId > 0) {
      let technician = technicianEntity
        .query()
        .where("technicianId", this.createdByTechnicianId)
        .first();
      return technician ? _clientFacingNameParts(technician.technicianId).firstName : "";
    }

    if (this.createdByContactId != null && this.createdByContactId > 0) {
      let contact = contactEntity
        .query<any>()
        .where("contactId", this.createdByContactId)
        .first();
      return contact ? contact.firstName : "";
    }

    return "";
  }

  get sentByLastName() {
    if (this.isCreatedByRule) {
      return "";
    }

    if (this.createdByTechnicianId != null && this.createdByTechnicianId > 0) {
      let technician = technicianEntity
        .query()
        .where("technicianId", this.createdByTechnicianId)
        .first();
      return technician ? _clientFacingNameParts(technician.technicianId).lastName : "";
    }

    if (this.createdByContactId != null && this.createdByContactId > 0) {
      let contact = contactEntity
        .query<any>()
        .where("contactId", this.createdByContactId)
        .first();
      return contact ? contact.lastName : "";
    }

    return "";
  }

  get getFullName() {
    if (this.createdByContactId != null && this.createdByContactId > 0) {
      let contact = contactEntity
        .query<any>()
        .where("contactId", this.createdByContactId)
        .first();
      let name = "";
      if (contact != null) {
        if (
          this.sourceCreatedByEmail != null &&
          this.sourceCreatedByEmail != ""
        ) {
          name =
            contact.fullNameWithoutClientName +
            `(${this.sourceCreatedByEmail})`;
        } else {
          name = contact.fullNameWithoutClientName;
        }
      }
      return name;
    } else {
      return this.sentByFirstName + " " + this.sentByLastName;
    }
  }

  get updatedByName() {
    if (this.updatedByTechnicianId != null && this.updatedByTechnicianId > 0) {
      let technician = technicianEntity
        .query()
        .where("technicianId", this.updatedByTechnicianId)
        .first();
      return technician ? technician.fullName : "";
    }
  }

  get isTechnician() {
    return this.createdByTechnicianId != null && this.createdByTechnicianId > 0;
  }

  get timeUTC() {
    return formatPortalChatMessageTimestamp(this.createdOn);
  }

  get relativeDateTime() {
    return formatPortalChatMessageTimestamp(this.createdOn);
  }

  get messageTimestampHover() {
    return formatPortalChatMessageTimestampHover(this.createdOn);
  }

  get isBeforeSevenDays() {
    const created = createdOnAsUtc(this.createdOn);
    if (!created) return false;
    const zoned = created.setZone(localTz());
    const currentDate = DateTime.utc().setZone(localTz());
    const diff = currentDate.diff(zoned, ["days", "hours"]);
    return diff.days >= 7;
  }

  get createdOnLabel() {
    const base = createdOnAsUtc(this.createdOn);
    if (!base) return "";

    const zoned = base.setZone(localTz());
    const currentDate = DateTime.utc().setZone(localTz());

    if (this.isBeforeSevenDays) {
      return zoned
        .setLocale(navigator.language)
        .toLocaleString(DateTime.DATE_FULL);
    }

    const yesterdayDate = DateTime.utc()
      .setZone(localTz())
      .minus({ days: 1 })
      .toISODate();
    if (zoned.toISODate() === currentDate.toISODate()) {
      return "Today";
    }
    if (zoned.toISODate() === yesterdayDate) {
      return "Yesterday";
    }
    return zoned.setLocale(navigator.language).toFormat("EEEE");
  }

  get timeSorterUTC() {
    const base = createdOnAsUtc(this.createdOn);
    if (!base) return "";
    const zoned = base.setZone(localTz());
    return zoned.toISO() || "";
  }

  get customUpdatedOn() {
    if (this.updatedOn != null) {
      const tz = localTz();
      let time = DateTime.fromISO(this.updatedOn, { zone: "utc" })
        .setZone(tz)
        .toFormat("hh:mm a");
      let updatedOn = DateTime.fromISO(this.updatedOn, {
        zone: "utc",
      }).setZone(tz);

      return (
        updatedOn
          .setLocale(navigator.language)
          .toLocaleString(DateTime.DATE_FULL) +
        " | " +
        time
      );
    }
    return "";
  }

  get customCreatedOn() {
    const base = createdOnAsUtc(this.createdOn);
    if (!base) return "";

    const currentDate = DateTime.utc().setZone(localTz()).toISODate();
    const yesterdayDate = DateTime.utc()
      .setZone(localTz())
      .minus({ days: 1 })
      .toISODate();
    const selectedDate = base.setZone(localTz()).toISODate();

    if (selectedDate === currentDate) return "Today";
    if (selectedDate === yesterdayDate) return "Yesterday";
    return "";
  }

  /** Local calendar day key for grouping timeline messages (yyyy-MM-dd). */
  get customCreatedOnDate() {
    const base = createdOnAsUtc(this.createdOn);
    if (!base) return;
    return base.setZone(localTz()).toISODate() ?? undefined;
  }

  get customTicketTimeDate() {
    let timeToConvert = this.ticketTimeDate;

    if (timeToConvert != null) {
      let convertedTime = DateTime.fromISO(timeToConvert, { zone: "utc" })
        .setZone(localTz())
        .setLocale(navigator.language)
        .toLocaleString(DateTime.DATE_FULL);
      return convertedTime;
    }
  }

  get customTicketTimeEndDate() {
    let timeToConvert = this.ticketTimeEndDate;
    if (timeToConvert != null) {
      let convertedTime = DateTime.fromISO(timeToConvert, { zone: "utc" })
        .setZone(localTz())
        .setLocale(navigator.language)
        .toLocaleString(DateTime.DATE_FULL);
      return convertedTime;
    }
  }

  get technicianName() {
    const loggedInId = loggedInTechnicianId();
    if (loggedInId != null && this.technicianId === loggedInId) {
      return "You";
    } else {
      let technician = technicianEntity
        .query()
        .where("technicianId", this.technicianId)
        .first();
      if (technician) {
        return technician.fullName;
      }
    }

    return "";
  }

  get customTimeInMinutes() {
    let minutes = this.timeInMinutes;
    return minutes > 0
      ? Duration.fromObject({ minutes: minutes }).toFormat("h 'hrs' m 'mins'")
      : "0 hrs 0 mins";
  }

  get timeSpentChanged() {
    if (
      this.isUpdate &&
      this.previousTimeSpentValue &&
      this.previousTimeSpentValue != ""
    ) {
      let object = JSON.parse(this.previousTimeSpentValue);
      if (object.timeInMinutes == this.timeInMinutes) {
        return false;
      }
      return true;
    }
    return true;
  }

  get isStartTimeAdded() {
    return (
      this.ticketTimeDate && this.ticketTimeDate.indexOf("T00:00:00") == -1
    );
  }

  get isEndTimeAdded() {
    return (
      this.ticketTimeEndDate &&
      this.ticketTimeEndDate.indexOf("T00:00:00") == -1
    );
  }

  get startTimeIn24Hours() {
    if (this.ticketTimeDate) {
      let startTimeIn24HourFormat = DateTime.fromISO(this.ticketTimeDate, {
        zone: "utc",
      })
        .setZone(localTz())
        .setLocale(navigator.language)
        .toFormat("HH:mm");
      return startTimeIn24HourFormat;
    }
  }

  get endTimeIn24Hours() {
    if (this.ticketTimeEndDate) {
      let startTimeIn24HourFormat = DateTime.fromISO(this.ticketTimeEndDate, {
        zone: "utc",
      })
        .setZone(localTz())
        .setLocale(navigator.language)
        .toFormat("HH:mm");
      return startTimeIn24HourFormat;
    }
  }

  get timeEntryComment() {
    let comment = this.comment;
    if (comment && comment != "") {
      if (comment.indexOf("style=") > -1) {
        let patternRegex = new RegExp("style=", "g");
        comment = comment.replace(patternRegex, "");
      }
      comment = marked.parse(comment);

      return comment;
    }
    return "";
  }

  get statusChangedReason() {
    if (this.statusChangedMessage && this.statusChangedMessage != "") {
      try {
        let object = JSON.parse(this.statusChangedMessage);
        if (object.reason) {
          return object.reason;
        }
      } catch (e) {}
    }
    return "";
  }

  get anyNonContactCcEmail() {
    if (this.contactCcEmail && this.contactCcEmail != "") {
      try {
        if (this.contactCcEmail.length > 0) {
          return true;
        }
        return false;
      } catch (e) {}
    }

    return false;
  }

  get getReactions() {
    let response: any[] = [];

    return response;
  }

  get checkpointTime() {
    if (
      this.type == "ShipmentEvent" &&
      this.checkpoint &&
      this.checkpoint.checkpointTime
    ) {
      let dateTime: string = this.checkpoint.checkpointTime;
      //If datetime is in ISO format
      if (!dateTime.includes("/")) {
        let showZone: boolean = dateTime.includes("+");

        //If datetime includes a zone
        if (showZone) {
          let date = DateTime.fromISO(dateTime, { setZone: true });
          return date.toFormat("MMM dd, y, t (ZZZZ)");
        }
        //If it's only date and time
        else {
          let date = DateTime.fromISO(dateTime, { zone: "utc" });
          return date.toFormat("MMM dd, y, t");
        }
      }
      //If datetime is in mm/DD/yyyy hh:mm:ss format
      else {
        let date = DateTime.fromFormat(dateTime, "D hh:mm:ss");
        if (!date.isValid) {
          dateTime = dateTime.split("+")[0];
          date = DateTime.fromISO(dateTime, { zone: "utc" });
        }
        return date.toFormat("MMM dd, y, t");
      }
    }
    return "";
  }
}

function getTechnicianNameByPov(
  technicianRef: number | unknown,
  _this: any,
): string {
  const technicianId = normalizeTechnicianIdRef(technicianRef);
  if (technicianId == null) {
    return "";
  }

  const loggedInId = loggedInTechnicianId();
  let technicianName: string = "";
  if (loggedInId != null && technicianId === loggedInId) {
    if (_this.eventCreatedByLoggedInUser) {
      technicianName = "yourself";
    } else {
      technicianName = "you";
    }
  } else {
    let technician = technicianEntity
      .query()
      .where("technicianId", technicianId)
      .first();
    technicianName = technician != null ? technician.fullName : "";
  }

  return technicianName;
}

function getStyleTagForOutboundIFrame(): string {
  return ``;
}
